import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import CommunityLayout from '../../../components/community/CommunityLayout';
import NoticeCard from '../../../components/common/NoticeCard';
import LoginModal from '../../../components/modals/LoginModal';
import { getPostList, PostListItem, ApiResponse, PostListResponse } from '../../../services/postApi';
import { getStudyCategoriesList, StudyCategory } from '../../../services/studyCategoryApi';
import { searchStudyPosts, PostSearchItem } from '../../../services/searchApi';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';

const Study: React.FC = () => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null); // 선택된 카테고리 ID
  const [pinnedPosts, setPinnedPosts] = useState<PostListItem[]>([]); // 공지사항
  const [regularPosts, setRegularPosts] = useState<PostListItem[]>([]); // 일반 게시글
  const [studyCategories, setStudyCategories] = useState<StudyCategory[]>([]); // 스터디 카테고리 목록
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true); // 카테고리 로딩 상태
  const [page, setPage] = useState(0);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  
  // API 호출 중복 방지용 ref
  const fetchingRef = useRef(false);
  
  // Redux 상태에서 로그인 정보 가져오기
  const { token } = useSelector((state: RootState) => state.auth);
  
  // 로그인 모달 상태
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // 로그인 후 이동할 게시글 ID 저장
  const [pendingPostId, setPendingPostId] = useState<number | null>(null);
  
  // 검색 관련 상태
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchResults, setSearchResults] = useState<PostSearchItem[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // 스터디 카테고리 목록 조회
  const fetchStudyCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      
      const response = await getStudyCategoriesList();
      /// console.log('스터디 카테고리 목록 API 응답:', {response});
      
      if (response.code === 200) {
        setStudyCategories(response.data);
      }
    } catch (error) {
      /// console.error('스터디 카테고리 목록 조회 실패:', error);
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  // 공지사항 조회
  const fetchPinnedPosts = useCallback(async () => {
    try {
      const response: ApiResponse<PostListResponse> = await getPostList(
        'study',
        0,
        10,
        undefined, // lectureCategoryId
        undefined, // lectureId
        undefined, // chapter
        undefined, // studyCategoryId - 공지사항은 카테고리 필터링 안함
        undefined // lecturerId
        // pinned 파라미터 제거 - 서버가 500 에러 반환
      );

      /// console.log('스터디 공지사항 API 응답:', {response});
      if (response.data) {
        const pinnedOnly = response.data.content.filter(post => post.pinned);
        setPinnedPosts(pinnedOnly);
      }
    } catch (error: any) {
      /// console.error('공지사항 조회 실패:', error);
    }
  }, []); // 의존성 배열 비움

  // 일반 게시글 조회
  const fetchRegularPosts = useCallback(async (pageNum: number = 0, reset: boolean = false) => {
    /// console.log('스터디 일반 게시글 API 호출:', { pageNum, reset, selectedCategory });
    
    // 중복 호출 방지
    if (fetchingRef.current) {
      /// console.log('이미 API 호출 중입니다.');
      return;
    }
    
    fetchingRef.current = true;
    setLoading(true);
    
    try {
      const response: ApiResponse<PostListResponse> = await getPostList(
        'study',
        pageNum,
        10,
        undefined, // lectureCategoryId
        undefined, // lectureId
        undefined, // chapter
        selectedCategory || undefined, // studyCategoryId - 카테고리 필터링 적용
        undefined // lecturerId
        // pinned 파라미터 제거 - 서버가 500 에러 반환
      );

      /// console.log('스터디 일반 게시글 API 응답:', {response});
      if (response.data) {
        const newPosts = response.data.content.filter(post => !post.pinned); // 클라이언트에서 필터링
        setRegularPosts(reset ? newPosts : prev => [...prev, ...newPosts]);
        setHasMorePosts(!response.data.last);
      }
    } catch (error: any) {
      /// console.error('일반 게시글 조회 실패:', error);
      if (error.message === 'Unauthorized') {
        navigate('/login');
        return;
      }
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [navigate, selectedCategory]);

  // 검색 함수
  const performSearch = useCallback(async (keyword: string) => {
    if (!keyword.trim()) {
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchQuery('');
      return;
    }

    setSearchLoading(true);
    setSearchError(null);
    setIsSearchMode(true);
    setSearchQuery(keyword);

    try {
      /// console.log('스터디 검색 실행:', keyword);
      const results = await searchStudyPosts(keyword);
      /// console.log('스터디 검색 결과:', results);
      setSearchResults(results);
    } catch (error) {
      /// console.error('스터디 검색 오류:', error);
      setSearchError('검색 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // 검색 초기화 함수
  const clearSearch = useCallback(() => {
    setSearchValue('');
    setSearchQuery('');
    setIsSearchMode(false);
    setSearchResults([]);
    setSearchError(null);
  }, []);

  // 검색어가 비어있으면 검색 모드 해제
  useEffect(() => {
    if (!searchValue.trim()) {
      clearSearch();
    }
  }, [searchValue, clearSearch]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchStudyCategories(); // 스터디 카테고리 로드
    fetchPinnedPosts(); // 공지사항 로드
    fetchRegularPosts(0, true); // 일반 게시글 초기 로드
  }, []); // 마운트 시 한 번만 실행

  // 선택된 카테고리가 변경될 때 일반 게시글만 새로고침
  useEffect(() => {
    if (selectedCategory !== null) { // null이 아닐 때만 실행
      setPage(0);
      setRegularPosts([]); // 기존 데이터 초기화
      fetchRegularPosts(0, true);
    }
  }, [selectedCategory]); // selectedCategory가 변경될 때만

  // 더 많은 데이터 로드
  const loadMorePosts = useCallback(() => {
    if (hasMorePosts && !loading && !isSearchMode) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchRegularPosts(nextPage, false);
    }
  }, [hasMorePosts, loading, isSearchMode, page, fetchRegularPosts]);

  // 무한스크롤 적용
  useInfiniteScroll({
    hasMore: hasMorePosts && !isSearchMode,
    isLoading: loading,
    onLoadMore: loadMorePosts,
    threshold: 200
  });

  // 게시글 클릭 핸들러
  const handlePostClick = (postId: number) => {
    // Redux와 localStorage 둘 다 확인
    const reduxToken = token;
    const localToken = localStorage.getItem('access_token');
    const finalToken = reduxToken || localToken;
    
    // 토큰이 없으면 로그인 모달 띄우기
    if (!finalToken) {
      setPendingPostId(postId); // 로그인 후 이동할 게시글 ID 저장
      setShowLoginModal(true);
      return;
    }
    
    // 토큰이 있으면 게시글 페이지로 이동
    navigate(`/community/post/${postId}`);
  };

  // 로그인 성공 후 핸들러
  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    
    // 로그인 후 대기 중인 게시글로 자동 이동
    if (pendingPostId) {
      navigate(`/community/post/${pendingPostId}`);
      setPendingPostId(null); // 상태 초기화
    }
  };

  // 로그인 모달 닫기 핸들러
  const handleLoginModalClose = () => {
    setShowLoginModal(false);
    setPendingPostId(null); // 모달을 닫을 때도 상태 초기화
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const searchTerm = (e.target as HTMLInputElement).value.trim();
      if (searchTerm) {
        // /// console.log(`검색: ${searchTerm}`);
        performSearch(searchTerm);
      } else {
        clearSearch();
      }
    }
  };

  // 렌더링을 위해 현재는 변수로 설정 (이미 state로 분리되어 있음)
  // const pinnedPosts = postList.filter(post => post.pinned);
  // const regularPosts = postList.filter(post => !post.pinned);

  return (
    <CommunityLayout>
      <Container>
        <MainContainer>
          {/* 페이지 헤더 */}
          <PageHeader>
            <PageTitle>스터디</PageTitle>
          </PageHeader>

          {/* 브레드크럼과 검색바 */}
          <NavigationBar>
            <BreadcrumbContainer>
              <BreadcrumbItem>홈</BreadcrumbItem>
              <ChevronIcon>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </ChevronIcon>
              <BreadcrumbItem>커뮤니티</BreadcrumbItem>
              <ChevronIcon>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </ChevronIcon>
              <BreadcrumbItem>스터디</BreadcrumbItem>
            </BreadcrumbContainer>
            
            <SearchContainer>
              <SearchBox>
                <SearchIcon>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path 
                      d="M14.87 14.87L10.4 10.4M12 6.5C12 9.54 9.54 12 6.5 12C3.46 12 1 9.54 1 6.5C1 3.46 3.46 1 6.5 1C9.54 1 12 3.46 12 6.5Z" 
                      stroke="rgba(55, 56, 60, 0.28)" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </SearchIcon>
                <SearchInput 
                  type="text" 
                  placeholder={searchLoading ? "검색 중..." : "검색어를 입력해주세요."}
                  value={searchValue}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchSubmit}
                  disabled={searchLoading}
                />
              </SearchBox>
            </SearchContainer>
          </NavigationBar>

          {/* 카테고리 필터 */}
          <CategoryFilterSection>
            {/* 카테고리 칩 필터 */}
            <ChipContainer>
              <ChipButton 
                active={selectedCategory === null} 
                onClick={() => setSelectedCategory(null)}
              >
                전체보기
              </ChipButton>
              {categoriesLoading ? (
                <LoadingText>카테고리 로딩 중...</LoadingText>
              ) : (
                studyCategories.map((category) => (
                  <ChipButton 
                    key={category.id}
                    active={selectedCategory === category.id} 
                    onClick={() => setSelectedCategory(category.id)}
                  >
                    {category.name}
                  </ChipButton>
                ))
              )}
            </ChipContainer>
          </CategoryFilterSection>

          {/* 상단고정 공지사항 */}
          {pinnedPosts.length > 0 && (
            <NoticeSection>
              {pinnedPosts.map(notice => (
                <NoticeCard 
                  key={notice.post_id}
                  title={notice.title}
                  onClick={() => handlePostClick(notice.post_id)}
                />
              ))}
            </NoticeSection>
          )}

          {/* 게시글 목록 */}
          <StudyList>
            {/* 검색 로딩 상태 */}
            {searchLoading ? (
              <LoadingContainer>
                <LoadingSpinner />
                <LoadingText>검색 중...</LoadingText>
              </LoadingContainer>
            ) : searchError ? (
              /* 검색 오류 표시 */
              <ErrorContainer>
                <ErrorText>{searchError}</ErrorText>
                <RetryButton onClick={() => performSearch(searchQuery)}>
                  다시 시도
                </RetryButton>
              </ErrorContainer>
            ) : isSearchMode ? (
              /* 검색 결과 표시 */
              searchResults.length > 0 ? (
                searchResults.map((study, index) => (
                  <React.Fragment key={study.id}>
                    <StudyCard onClick={() => handlePostClick(study.id)}>
                      <StudyContent>
                        <StudyHeader>
                          <StudyTitle>{study.title}</StudyTitle>
                          <StudyDescription
                            dangerouslySetInnerHTML={{ __html: study.htmlcontent.replace(/<[^>]*>/g, '') }}
                          />
                        </StudyHeader>
                        
                        <StudyMeta>
                          <StudyCategoryTags>
                            <StudyCategoryText>{study.category}</StudyCategoryText>
                            {study.studyCategoryName && (
                              <>
                                <Divider />
                                <StudyType>{study.studyCategoryName}</StudyType>
                              </>
                            )}
                          </StudyCategoryTags>
                        </StudyMeta>
                      </StudyContent>
                      
                      <StudyFooter>
                        <StudyAuthorInfo>
                          <AuthorAvatar>
                            <img src={study.writerProfileImageUrl || "/images/default-avatar.png"} alt="작성자" />
                          </AuthorAvatar>
                          <AuthorName>{study.writerName}</AuthorName>
                          <Divider />
                          <StudyDate>{new Date(study.postDate).toLocaleString('ko-KR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                          })}</StudyDate>
                        </StudyAuthorInfo>
                        
                        <StudyStats>
                          <StatItem>댓글 -</StatItem>
                          <Divider />
                          <StatItem>조회수 -</StatItem>
                        </StudyStats>
                      </StudyFooter>
                    </StudyCard>
                    
                    {index < searchResults.length - 1 && <StudyDivider />}
                  </React.Fragment>
                ))
              ) : (
                /* 검색 결과가 없을 때 */
                <EmptySearchContainer>
                  <EmptySearchText>'{searchQuery}' 에 대한 검색 결과가 없습니다.</EmptySearchText>
                  <EmptySearchSubText>다른 검색어로 다시 시도해보세요.</EmptySearchSubText>
                </EmptySearchContainer>
              )
            ) : (
              /* 일반 모드 - 기존 게시글 목록 */
              regularPosts.length > 0 ? (
                regularPosts.map((study, index) => (
                  <React.Fragment key={study.post_id}>
                    <StudyCard onClick={() => handlePostClick(study.post_id)}>
                      <StudyContent>
                        <StudyHeader>
                          <StudyTitle>{study.title}</StudyTitle>
                          <StudyDescription>
                            {study.content.replace(/<[^>]*>/g, '')} {/* HTML 태그 제거 */}
                          </StudyDescription>
                        </StudyHeader>
                        
                        <StudyMeta>
                          <StudyCategoryTags>
                            {/* study_category가 있으면 표시, 없으면 post_category 표시 */}
                            {(study.study_category || study.post_category) && (
                              <StudyCategoryText>
                                {study.study_category || study.post_category}
                              </StudyCategoryText>
                            )}
                            {study.study_name && (
                              <>
                                <Divider />
                                <StudyType>{study.study_name}</StudyType>
                              </>
                            )}
                          </StudyCategoryTags>
                        </StudyMeta>
                      </StudyContent>
                      
                      <StudyFooter>
                        <StudyAuthorInfo>
                          <AuthorAvatar>
                            <img src={study.writer_profile_image_url || "/images/default-avatar.png"} alt="작성자" />
                          </AuthorAvatar>
                          <AuthorName>{study.writer_name}</AuthorName>
                          <Divider />
                          <StudyDate>{new Date(study.created_at).toLocaleString('ko-KR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                          })}</StudyDate>
                        </StudyAuthorInfo>
                        
                        <StudyStats>
                          <StatItem>댓글 {study.reply_cnt || 0}</StatItem>
                          <Divider />
                          <StatItem>조회수 {study.view_cnt}</StatItem>
                        </StudyStats>
                      </StudyFooter>
                    </StudyCard>
                    
                    {index < regularPosts.length - 1 && <StudyDivider />}
                  </React.Fragment>
                ))
              ) : (
                /* 일반 모드에서 게시글이 없을 때 */
                <EmptyContainer>
                  <EmptyText>아직 등록된 스터디가 없습니다.</EmptyText>
                  <EmptySubText>첫 번째 스터디를 등록해보세요!</EmptySubText>
                </EmptyContainer>
              )
            )}
          </StudyList>

          {/* 무한스크롤 로딩 표시 */}
          {loading && regularPosts.length > 0 && !isSearchMode && (
            <LoadingContainer>
              <LoadingSpinner />
              <LoadingText>추가 게시글을 불러오는 중...</LoadingText>
            </LoadingContainer>
          )}
        </MainContainer>
        
        {/* 로그인 모달 */}
        <LoginModal
          isOpen={showLoginModal}
          onClose={handleLoginModalClose}
          onLoginSuccess={handleLoginSuccess}
        />
      </Container>
    </CommunityLayout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
`;

const MainContainer = styled.div`
  width: 100%;
  max-width: 894px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  margin-top: 60px;
`;

const PageTitle = styled.h1`
  color: #171719;
  font-size: 40px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 52px;
  margin: 0;
`;

const NavigationBar = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const BreadcrumbItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const ChevronIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchContainer = styled.div`
  width: 282px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchBox = styled.div`
  width: 100%;
  padding: 8px;
  background: white;
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  outline: 1px solid rgba(112, 115, 124, 0.22);
  outline-offset: -1px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const SearchIcon = styled.div`
  width: 16px;
  height: 16px;
  padding: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: #171719;
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16.01px;
  letter-spacing: 0.30px;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CategoryFilterSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const NoticeSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: -32px;
`;

const StudyList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StudyCard = styled.div`
  width: 100%;
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  cursor: pointer;
`;

const StudyContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StudyHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StudyTitle = styled.h3`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  margin: 0;
`;

const StudyDescription = styled.p`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 21.99px;
  letter-spacing: 0.20px;
  margin: 0;
  
  /* 3줄 말줄임 */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StudyMeta = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const StudyCategoryTags = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const StudyCategoryText = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const StudyType = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const StudyRecruitStatus = styled.span<{ isCompleted: boolean }>`
  color: ${props => props.isCompleted ? 'rgba(55, 56, 60, 0.41)' : '#448181'};
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const StudyFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StudyAuthorInfo = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const AuthorAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  outline: 1px solid rgba(112, 115, 124, 0.08);
  outline-offset: -1px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AuthorName = styled.span`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const StudyDate = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const StudyStats = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const StatItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const Divider = styled.div`
  width: 1px;
  height: 12px;
  background: rgba(112, 115, 124, 0.22);
`;

const StudyDivider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(112, 115, 124, 0.22);
`;

const LoadMoreContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 32px;
`;

const LoadMoreButton = styled.button<{ disabled: boolean }>`
  padding: 12px 24px;
  background: ${props => props.disabled ? '#f5f5f5' : '#448181'};
  color: ${props => props.disabled ? '#999' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s ease;

  &:hover {
    background: ${props => props.disabled ? '#f5f5f5' : '#3a6b6b'};
  }
`;

// 검색 관련 스타일 컴포넌트
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
`;

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #448181;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
`;

const ErrorText = styled.span`
  color: #d32f2f;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  text-align: center;
`;

const RetryButton = styled.button`
  padding: 8px 16px;
  background: #448181;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #3a6b6b;
  }
`;

const EmptySearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 8px;
`;

const EmptySearchText = styled.span`
  color: #171719;
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 26px;
  text-align: center;
`;

const EmptySearchSubText = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 8px;
`;

const EmptyText = styled.span`
  color: #171719;
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 26px;
  text-align: center;
`;

const EmptySubText = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
`;

const ChipContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  min-height: 38px; /* 버튼 높이에 맞는 최소 높이 보장 */
  contain: layout;
`;

const ChipButton = styled.button<{ active: boolean }>`
  position: relative;
  padding: 7px 11px;
  background: ${props => props.active ? '#171719' : 'white'};
  color: ${props => props.active ? '#F7F7F8' : 'rgba(55, 56, 60, 0.61)'};
  border: 1px solid ${props => props.active ? '#171719' : 'rgba(112, 115, 124, 0.16)'};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 15px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 22.01px;
  letter-spacing: 0.14px;
  box-sizing: border-box;
  min-width: fit-content;
  white-space: nowrap;
  flex-shrink: 0;
  
  /* 레이아웃 안정화를 위한 스타일 */
  will-change: background-color, color, border;
  contain: layout style;
  
  &:hover {
    background: ${props => props.active ? '#171719' : 'rgba(112, 115, 124, 0.08)'};
  }
`;

const LoadingText = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 15px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 22.01px;
  letter-spacing: 0.14px;
  padding: 7px 11px;
`;

export default Study;
